import React, { useState } from "react";
import { Transaction } from "@/types/entities";
import { X } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

type EditReceiptModalProps = {
  transaction: Transaction;
  onClose: () => void;
  onSaveAndVerify: (transaction: Transaction) => void;
  onDelete: (receiptId: string) => Promise<void>;
};

const EditReceiptModal: React.FC<EditReceiptModalProps> = ({
  transaction,
  onClose,
  onSaveAndVerify,
  onDelete,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      await onDelete(transaction.receipt_id ?? '');
      setSuccess("Receipt deleted successfully.");
      onClose();
    } catch (err) {
      setError("Failed to delete receipt. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditReceipt = () => {
    // Redirect to the ReceiptDetailPage with the transaction's receipt_id
    if (transaction.receipt_id) {
      navigate(`/receipt/${transaction.receipt_id}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-gray-900 p-6 rounded shadow-lg w-96 text-white relative">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-white"
          onClick={onClose}
        >
          <X size={24} />
        </button>
        <div className="mb-4">
          <h1 className="text-lg font-bold">{transaction.name}</h1>
        </div>
        <div className="mb-4">
          <p className="text-gray-400">${(transaction.amount_cents / 100).toFixed(2)}</p>
        </div>
        {error && <p className="text-red-500 mb-2">{error}</p>}
        {success && <p className="text-green-500 mb-2">{success}</p>}
        <div className="flex flex-col space-y-2">
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            onClick={() => onSaveAndVerify(transaction)}
          >
            Save and Verify
          </button>
          <button
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded"
            onClick={handleEditReceipt}
          >
            Edit Receipt
          </button>
          <button
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Receipt"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditReceiptModal;